import type { Activity, MeetingView } from '@sorted/types'
import { useEffect, useState } from 'react'
import { ACTIVITY_ICON, Spinner } from '../components/icons.js'
import { OriginPicker, type PickedOrigin } from '../components/OriginPicker.js'
import { Screen } from '../components/Screen.js'
import { api } from '../lib/api.js'
import { activityLabel, meetingWhenLong } from '../lib/format.js'
import { recallOrigin, rememberOrigin } from '../lib/geo.js'
import { t } from '../lib/i18n.js'

/**
 * Join + origin - spec §11, the highest-risk screen in the funnel.
 *
 * "Share location is one tap. 'Where will you be coming from Thursday?' is a
 *  text field, and text fields are where consumer funnels die."
 *
 * GPS stays the primary path, but "somewhere else" is one tap away and never
 * buried: Bob may be opening the link at work for a trip he will make from
 * home.
 */

interface Props {
  token: string
  meeting: MeetingView
  onResolved: (view: MeetingView) => void
}

export function Origin({ token, meeting, onResolved }: Props) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const remembered = recallOrigin()

  const activity = meeting.activity as Activity
  const Icon = ACTIVITY_ICON[activity]

  // Opening the link IS the join - Bob never signs up (invariant 4).
  useEffect(() => {
    void api.join(token).catch(() => {})
  }, [token])

  async function submit(origin: PickedOrigin) {
    setBusy(true)
    setError(null)
    try {
      rememberOrigin(origin, origin.label)
      onResolved(await api.submitOrigin(token, origin, origin.label))
    } catch {
      setBusy(false)
      setError(t('origin.failed'))
    }
  }

  return (
    <Screen>
      {/* The invitation, centred and given room. Bob opened a link with no
          context, so this block is the whole explanation of where he is -
          what, when, and that someone chose to send it to him. */}
      <div className="invite center">
        <p className="invite-intro">{t('origin.invited')}</p>
        <h1 className="title-with-icon invite-what">
          <Icon size={34} strokeWidth={1.5} aria-hidden />
          {activityLabel(activity)}
        </h1>
        <p className="when-line">{meetingWhenLong(meeting.meetAt)}</p>
      </div>

      <div className="mt">
        <h2>{t('origin.question')}</h2>
        <p className="muted">{t('origin.hint')}</p>
      </div>

      {/* Disclosure above the buttons it describes: sharing a location is the
          tap this line is about, and underneath it is read after the fact.
          Still one line, never a consent modal (§11). */}
      <p className="faint mt">{t('origin.privacy')}</p>

      {error && (
        <p className="faint error" role="status">
          {error}
        </p>
      )}

      <div className="mt-sm">
        {busy ? (
          <button className="btn btn-primary" disabled>
            <Spinner />
            {t('origin.searching')}
          </button>
        ) : (
          <OriginPicker remembered={remembered} busy={busy} onPick={submit} />
        )}
      </div>
    </Screen>
  )
}
